import { useCallback, useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
export const THEME_STORAGE_KEY = 'atte.theme';

const listeners = new Set<() => void>();

function read(): ThemePreference {
  try {
    const v = window.localStorage.getItem(THEME_STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

/** Aplica la preferencia en <html data-theme> y la guarda (misma lógica que /theme-init.js). */
export function setThemePreference(pref: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    /* almacenamiento bloqueado */
  }
  const root = document.documentElement;
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);
  listeners.forEach((l) => l());
}

export function getThemePreference(): ThemePreference {
  return typeof window === 'undefined' ? 'system' : read();
}

/** Tema efectivo (resuelve "system" con prefers-color-scheme). */
export function getResolvedTheme(): 'light' | 'dark' {
  const pref = getThemePreference();
  if (pref !== 'system') return pref;
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  const mql = typeof window !== 'undefined' ? window.matchMedia?.('(prefers-color-scheme: dark)') : undefined;
  mql?.addEventListener('change', cb);
  return () => {
    listeners.delete(cb);
    mql?.removeEventListener('change', cb);
  };
}

/** Preferencia y tema efectivo, reactivos. */
export function useTheme(): {
  preference: ThemePreference;
  resolved: 'light' | 'dark';
  setPreference: (p: ThemePreference) => void;
} {
  const preference = useSyncExternalStore(subscribe, getThemePreference, () => 'system' as const);
  const resolved = useSyncExternalStore(subscribe, getResolvedTheme, () => 'light' as const);
  const setPreference = useCallback((p: ThemePreference) => setThemePreference(p), []);
  return { preference, resolved, setPreference };
}
